export enum ENVIRONMENT {
  PRODUCTION = "production",
  DEV = "dev",
}

export type User = {
  id: string;
  email: string;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  permissions: string[];
  organisation: string;
  isFirstLogin?: boolean;
  planException: unknown;
  organisationDetails: Record<string, string>;
  status?: "Active" | "Inactive";
  defaultProduct?: "unified" | "sales";
};

export enum IntroEmailTypes {
  OUTBOUND = "OUTBOUND",
  OUTBOUND_WITH_CONTEXT = "OUTBOUND_WITH_CONTEXT",
  INBOUND = "INBOUND",
}

export type PrimitiveType = number | boolean | string;

export type PrimitiveObject = Record<string, PrimitiveType>;

export enum Platform {
  SALES = "sales",
  MARKETING = "marketing",
}

export enum TemplateType {
  AIDA = "AIDA",
  PAS = "PAS",
  BLOG_POST_INTRO_PARA = "BLOG_POST_INTRO_PARA",
  BLOG_POST_TOPIC_IDEAS = "BLOG_POST_TOPIC_IDEAS",
  BLOG_POST_OUTLINE = "BLOG_POST_OUTLINE",
  PERFECT_HEADLINE = "PERFECT_HEADLINE",
  BLOG_POST_CONCLUSION = "BLOG_POST_CONCLUSION",
  FACEBOOK_AD_HEADLINE = "FACEBOOK_AD_HEADLINE",
  FACEBOOK_AD_PRIMARY_TEXT = "FACEBOOK_AD_PRIMARY_TEXT",
  GOOGLE_AD_HEADLINE = "GOOGLE_AD_HEADLINE",
  GOOGLE_AD_DESCRIPTION = "GOOGLE_AD_DESCRIPTION",
  COLD_EMAIL = "COLD_EMAIL",
  SUBJECT_LINE = "SUBJECT_LINE",
  VALUE_PROPS = "VALUE_PROPS",
  INTRO_LINES = "INTRO_LINES",
  FOLLOW_UP_EMAILS = "FOLLOW_UP_EMAILS",
  FOLLOW_UP_SNIPPETS = "FOLLOW_UP_SNIPPETS",
  PAIN_POINTS = "PAIN_POINTS",
  REPHRASE = "REPHRASE",
  REFRESH_EMAIL = "REFRESH_EMAIL",
  LENGTHEN = "LENGTHEN",
  SHORTEN = "SHORTEN",
  WRITE_MORE_PREFIX = "WRITE_MORE_PREFIX",
  WRITE_MORE_MID = "WRITE_MORE_MID",
  WRITE_MORE_SUFFIX = "WRITE_MORE_SUFFIX",
  LINKEDIN_BLOG_POST_INTRO_PARA = "LINKEDIN_BLOG_POST_INTRO_PARA",
  LINKEDIN_BLOG_POST_TOPIC_IDEAS = "LINKEDIN_BLOG_POST_TOPIC_IDEAS",
  LINKEDIN_BLOG_POST_OUTLINE = "LINKEDIN_BLOG_POST_OUTLINE",
  LINKEDIN_BLOG_POST_CONCLUSION = "LINKEDIN_BLOG_POST_CONCLUSION",
  LINKEDIN_COLD_EMAIL = "LINKEDIN_COLD_EMAIL",
  LINKEDIN_CONNECTION_REQUEST = "LINKEDIN_CONNECTION_REQUEST",
  REFERRAL_EMAILS = "REFERRAL_EMAILS",
  BREAKUP_EMAILS = "BREAKUP_EMAILS",
  MULTIPLE_CHOICE_EMAILS = "MULTIPLE_CHOICE_EMAILS",
  PRODUCT_DESCRIPTION = "PRODUCT_DESCRIPTION",
  OUTBOUND_INTRO_EMAILS = "OUTBOUND_INTRO_EMAILS",
  GENERAL_INBOUND_EMAILS = "GENERAL_INBOUND_EMAILS",
  INBOUND_FORM_EMAILS = "INBOUND_FORM_EMAILS",
  INBOUND_DEMO_EMAILS = "INBOUND_DEMO_EMAILS",
  INBOUND_DOWNLOAD_EMAILS = "INBOUND_DOWNLOAD_EMAILS",
  INBOUND_LINKEDIN_EMAILS = "INBOUND_LINKEDIN_EMAILS",
  INBOUND_TRAINING_COURSE_EMAILS = "INBOUND_TRAINING_COURSE_EMAILS",
  INBOUND_VIDEO_EMAILS = "INBOUND_VIDEO_EMAILS",
  INBOUND_WEBSITE_EMAILS = "INBOUND_WEBSITE_EMAILS",
  PERSONALIZATION_CONTENT = "PERSONALIZATION_CONTENT",
  THREE_P_PROBLEMS = "THREE_P_PROBLEMS",
  THREE_P_PROOF = "THREE_P_PROOF",
  THREE_P_PERSUADE = "THREE_P_PERSUADE",
  EVENTS_INVITATION = "EVENTS_INVITATION",
  EVENTS_CHECK_ATTENDANCE = "EVENTS_CHECK_ATTENDANCE",
  EVENTS_PRE_EVENT_REMINDER = "EVENTS_PRE_EVENT_REMINDER",
  EVENTS_POST_EVENT_ALL = "EVENTS_POST_EVENT_ALL",
  EVENTS_POST_EVENT_ATTENDED = "EVENTS_POST_EVENT_ATTENDED",
  EVENTS_POST_EVENT_NOT_ATTENDED = "EVENTS_POST_EVENT_NOT_ATTENDED",
  FOLLOWUP_GENERAL = "FOLLOWUP_GENERAL",
  FOLLOWUP_NURTURE = "FOLLOWUP_NURTURE",
  FOLLOWUP_POST_TRAINING = "FOLLOWUP_POST_TRAINING",
  CUSTOMER_SUCCESS_SURVEY = "CUSTOMER_SUCCESS_SURVEY",
  CUSTOMER_SUCCESS_RENEWAL = "CUSTOMER_SUCCESS_RENEWAL",
  BLOG_POST = "BLOG_POST",
  BLOG_TITLE = "BLOG_TITLE",
  BLOG_POST_INTRO_PARA_COMPLETION = "BLOG_POST_INTRO_PARA_COMPLETION",
}

export enum TemplateCategory {
  EMAIL_ASSISTANT = "Email Assistant",
  LINKEDIN_ASSISTANT = "LinkedIn Assistant",
  MARKETING = "Marketing",
  ACTION = "Action",
}

export enum SORT_ORDER {
  ASCENDING = 1,
  DESCENDING = -1,
}

export type OpenAICompletionResponse = {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: {
    text: string;
    finish_reason: string;
    logprobs?: any;
  }[];
};

export type TemplateField = {
  id: string;
  key: string;
  title: string;
  required?: boolean;
  characterLimit: number;
  placeholder?: string;
  fieldType?: "input" | "textarea";
  info?: {
    text: string;
  };
};

export interface TemplateResponse {
  id: TemplateType;
  title: string;
  description: string;
  imageUrl: string;
  platform: Platform;
  fields: TemplateField[];
  category: TemplateCategory;
}

export interface TemplateRequest {
  id: TemplateType;
}

export interface TagsResponse {
  tags: string[];
}

export interface ContentResponse {
  id: string;
  userId: string;
  templateId: TemplateType;
  fields: Record<string, string>;
  openAIText: string;
  userEditedText: string;
  isFlagged?: boolean;
  flaggedMessage?: string;
  isFavorite?: boolean;
  tags: string[];
  createdAt: number;
  updatedAt: number;
  isNew?: boolean;
}

export interface TextActionResponse {
  id: string;
  text: string;
  similarityScore?: number;
}

export interface GenerateContentRequest {
  templateId: TemplateType;
  fields: Record<string, string>;
  extraOptions?: {
    n?: number;
  };
  grammarCheck?: boolean;
}

export interface EditContentRequest {
  text: string;
}

export interface PaginationParams {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: SORT_ORDER;
}

export interface GetContentRequest extends PaginationParams {
  contentId?: string;
  templateId?: string;
  searchQuery?: string;
  favorite?: boolean;
  deleted?: boolean;
  platform?: Platform;
}

export interface AutoCompleteRequest {
  email: string;
  company?: string;
  suffix?: string;
  subject?: string;
}

export interface LinkedInMessageRequest {
  senderName: string;
  aboutSender: string;
  recipientName: string;
  aboutRecipient: string;
  productName?: string;
  productDescription?: string;
}

export interface LinkedInConnectionRequest {
  senderName: string;
  aboutSender?: string;
  recipientName: string;
  aboutRecipient: string;
  commonInterest?: string;
  purpose?: string;
}

export interface GetTemplateRequest {
  id?: TemplateType;
  platform?: Platform;
}

export type ProofPoint = {
  text: string;
  url?: string;
};

export interface GenerateEmailRequest {
  description: string;
  numberOfOutputs: number;
  painPoints: string[];
  valueProps: string[];
  proofPoint?: ProofPoint;
  additionalContext?: string;
}
export interface GenerateIntroEmailsRequest extends GenerateEmailRequest {
  companyName: string;
  keyPoints?: string[];
  intentSource?: string;
  additionalInfo?: string;
  downloadSummary?: string;
  downloadRelevance?: string;
  demoDetails?: string;
  formSummary?: string;
  videoSummary?: string;
  videoRelevance?: string;
  trainingCourseTitle?: string;
  trainingCourseStatus?: string;
}

export interface GenerateFollowupEmailRequest extends GenerateEmailRequest {
  previousEmail: string;
}

export interface GenerateSubjectLineRequest {
  companyName: string;
  emailBody: string;
}
